import {
  IntelligentActionContentResult,
  SitePlanArrayDetailsContent,
  SitePlanArrayReviewDraft,
  UpdateIntelligentActionContentResult,
} from '@intellio/shared/models';
import { cloneDeep } from 'lodash';
import { compareIntelligentActionResults } from './compare-intelligent-action-result.utility';

export type SitePlanArrayResultState =
  | 'missing'
  | 'criteria'
  | 'error'
  | 'unsupported'
  | 'malformed'
  | 'ready';

export function sitePlanArrayInstance(energySourceId: string): number | null {
  return energySourceId === 'Solar'
    ? 0
    : energySourceId === 'EnergyStorageDER'
    ? 1
    : null;
}

function evidenceIsValid(value: any): boolean {
  return (
    value &&
    typeof value.id === 'string' &&
    (value.label === null || typeof value.label === 'string') &&
    ['panelCount', 'tilt', 'azimuth'].every(
      (key) =>
        value[key] === null ||
        (typeof value[key] === 'number' && Number.isFinite(value[key]))
    ) &&
    value.rawValues &&
    ['panelCount', 'tilt', 'azimuth'].every(
      (key) =>
        value.rawValues[key] === null ||
        typeof value.rawValues[key] === 'string'
    ) &&
    Array.isArray(value.pageNumbers) &&
    value.pageNumbers.every((page) => Number.isInteger(page) && page > 0) &&
    typeof value.analysis === 'string'
  );
}

/** Failure envelopes need not have a discriminator or collections. */
export function sitePlanArrayResultState(
  result?: IntelligentActionContentResult
): SitePlanArrayResultState {
  if (!result) return 'missing';
  if (result.resultStatus === 'FailedCriteria') return 'criteria';
  const content = result.content;
  if (result.resultStatus === 'Error' || content?.success === false)
    return 'error';
  if (!content || content.success !== true) return 'malformed';
  if (
    content.contentType !== 'sitePlanArrayDetails' ||
    content.schemaVersion !== 1
  )
    return 'unsupported';
  if (
    !Array.isArray(content.comparisons) ||
    !Array.isArray(content.unmatchedSitePlanArrays) ||
    typeof content.analysis !== 'string' ||
    !content.metadata ||
    !content.summary ||
    typeof content.summary.allMatched !== 'boolean' ||
    ![
      'applicationArrayCount',
      'sitePlanArrayCount',
      'matchedCount',
      'unmatchedApplicationCount',
      'unmatchedSitePlanCount',
    ].every(
      (key) =>
        Number.isInteger(content.summary[key]) && content.summary[key] >= 0
    )
  )
    return 'malformed';
  const ids = new Set<string>();
  for (const child of content.comparisons) {
    if (
      !child ||
      typeof child.comparisonId !== 'string' ||
      !child.comparisonId ||
      ids.has(child.comparisonId) ||
      typeof child.applicationArrayId !== 'string' ||
      !Number.isInteger(child.applicationOrder) ||
      child.applicationOrder < 0 ||
      child.success !== true ||
      !evidenceIsValid(child.applicationArray) ||
      !(child.sitePlanArray === null || evidenceIsValid(child.sitePlanArray)) ||
      !['Matched', 'Unmatched'].includes(child.matchStatus) ||
      !['Open', 'Approved', 'Flagged'].includes(child.resultStatus) ||
      !(child.isCorrect === null || typeof child.isCorrect === 'boolean') ||
      !['actualValue', 'extractedValue', 'currentValue', 'analysis'].every(
        (key) => typeof child[key] === 'string'
      )
    )
      return 'malformed';
    ids.add(child.comparisonId);
  }
  return content.unmatchedSitePlanArrays.every(
    (item) =>
      item &&
      evidenceIsValid(item.sitePlanArray) &&
      typeof item.analysis === 'string'
  )
    ? 'ready'
    : 'malformed';
}

export function buildSitePlanArrayReviewUpdate(
  result: IntelligentActionContentResult,
  draft: SitePlanArrayReviewDraft
): UpdateIntelligentActionContentResult | null {
  if (
    result.ruleName !== 'SitePlanArrayDetails' ||
    sitePlanArrayResultState(result) !== 'ready' ||
    !draft ||
    draft.applicationId !== result.applicationId ||
    draft.instanceId !== result.instanceId ||
    !draft.values ||
    !result.content.comparisons.length
  )
    return null;
  const content: SitePlanArrayDetailsContent = cloneDeep(result.content);
  let reviewed = false;
  for (const child of content.comparisons) {
    if (
      !Object.prototype.hasOwnProperty.call(draft.values, child.comparisonId) ||
      typeof draft.values[child.comparisonId] !== 'string'
    )
      continue;
    reviewed = true;
    child.currentValue = draft.values[child.comparisonId];
    child.isCorrect = compareIntelligentActionResults(
      child.extractedValue,
      child.currentValue
    );
    child.resultStatus = child.isCorrect ? 'Approved' : 'Flagged';
  }
  if (!reviewed) return null;
  const children = content.comparisons;
  const resultStatus = children.some(
    (child) => child.resultStatus === 'Flagged'
  )
    ? 'Flagged'
    : children.some(
        (child) => child.resultStatus === 'Open' || child.isCorrect === null
      )
    ? 'Open'
    : 'Approved';
  const isCorrect = children.some((child) => child.isCorrect === false)
    ? false
    : children.every(
        (child) => child.resultStatus !== 'Open' && child.isCorrect === true
      )
    ? true
    : null;
  return {
    applicationId: result.applicationId,
    ruleName: result.ruleName,
    instanceId: result.instanceId,
    isAIGenerated: result.isAIGenerated,
    content,
    resultStatus,
    isCorrect,
  };
}
